<template>
    <div class="notification">
        <div :class="{ shadow: scrolled }" class="header">
            <div class="search-container">
                <Search class="search"
                        placeholder="Поиск по продуктам"
                        @search="onInputQuery" />

                <button v-if="mobile" class="no-border action" @click="filterSheet = 'HALF_VISIBLE'">
                    <IconSettings class="icon" />
                </button>
            </div>


            <div v-if="!mobile" class="filter">
                <div class="row">
                    <div class="item-list">
                        <InlineMultiSelect v-model="model.eventTypes"
                                           :options="optionsEventTypes"
                                           :toTitle="it => it.name"
                                           class="item"
                                           label="Тип уведомления" />

                        <InlineDatePicker v-model="date"
                                          class="item"
                                          label="Дата"
                                          select-range />
                    </div>

                    <button :disabled="emptyFilters" class="no-border action" @click="onClear">
                        <IconFilterClear class="icon" />
                    </button>
                </div>
                <div class="row">
                    <Toggle v-model="model.onlyUnread">Только непрочитанные</Toggle>
                </div>
            </div>
        </div>

        <div ref="middle" class="middle scroll">
            <InfinityScroll ref="scroll" :load-next="loadNextPage" root=".notification .middle">
                <div v-if="notFound && this.nextPage !== 0" class="not-found">
                    <template v-if="model.onlyUnread">
                        <div class="title">Непрочитанных уведомлений нет</div>
                    </template>

                    <template v-else>
                        <div class="title">Ничего не найдено</div>
                        <div class="desc">
                            Нет ни одного совпадения по запросу.<br>
                            Измените критерии поиска.
                        </div>
                    </template>
                </div>

                <div v-else-if="notFound && emptyFiltersAndQuery" class="no-elements">
                    <div class="icon"></div>
                    <div class="text">Уведомлений пока нет</div>
                </div>

                <template v-else>
                    <CardNotification v-for="it in elements"
                                      :key="it.id"
                                      ref="cardNotify"
                                      :value="it"
                                      @closeModal="close"
                                      @read="onRead" />
                </template>

                <template #loader>
                    <CardNewsSkeleton v-for="it in 5" v-show="!notFound" :key="it" class="card-skeleton" />
                </template>
            </InfinityScroll>
        </div>

        <div v-show="!notFound" class="footer">
            <button :disabled="unreadNotifyCount === 0" class="action no-border" @click="readAll">
                <span class="text">Сделать прочитанными</span>
                <IconEyeLarge class="icon" />
            </button>
        </div>

        <BottomSheet v-if="mobile" v-model="filterSheet" class="filter-sheet">
            <template #header>
                <div class="title">Фильтры</div>
                <button :disabled="emptyFilters"
                        class="no-border iconed"
                        @click="onClear">
                    <IconFilterClear />
                </button>
            </template>
            <template>
                <MobileInlineMultiSelect v-model="model.eventTypes"
                                         :options="dicts.eventTypes"
                                         :toTitle="it => it.name"
                                         class="item"
                                         label="Тип уведомления" />

                <MobileInlineDatePicker v-model="date"
                                        class="item"
                                        label="Дата"
                                        select-range />

                <Toggle v-model="model.onlyUnread" class="item">Только непрочитанные</Toggle>
            </template>
        </BottomSheet>
    </div>
</template>

<script>
import CardNewsSkeleton from '@/common/skeletons/CardNewsSkeleton.vue';
import Search from '@/common/comp/search/Search.vue';
import { MODERATE_CARD } from '@/common/rights.js';
import { ADMIN } from '@/common/role.ids.js';
import { hasRights, hasRoles } from '@/common/session.js';
import { debounceAsync } from '@dsee/util/async.js';
import { read, readAll, search } from '@/restapi/notify.js';
import CardNotification from './CardNotification.vue';
import IconSettings from './icons/settings.svg?component';

const EVENT_TYPES = Object.freeze([
    { name: 'Создан продукт', id: 'PRODUCT_CREATED', right: [ MODERATE_CARD ], role: [] },
    { name: 'Создан черновик', id: 'DRAFT_CREATED', right: [ MODERATE_CARD ], role: [] },
    { name: 'Опубликован продукт', id: 'PRODUCT_PUBLISHED', right: [], role: [] },
    { name: 'Опубликован черновик', id: 'DRAFT_PUBLISHED', right: [], role: [] },
    { name: 'Возврат из архива', id: 'PRODUCT_UNARCHIVED', right: [], role: [] },
    // { name: 'Удален продукт', id: 'PRODUCT_DELETED', right: [], role: [] },
    { name: 'Удален черновик', id: 'DRAFT_DELETED', right: [], role: [] },
    { name: 'Комментарии', id: 'MESSAGE', right: [], role: [] },
    // { name: 'Добавлена оценка', id: 'RATED', right: [], role: [] },
    { name: 'Создан новый тег', id: 'TAG_ADDED', right: [], role: [ ADMIN ] },
    { name: 'Добавлен синоним', id: 'TAG_ALIAS_ADDED', right: [], role: [ ADMIN ] },
]);

export default {
    name: 'Notification',
    components: {
        CardNewsSkeleton,
        Search,
        CardNotification,
        IconSettings,
    },
    data() {
        return {
            model: {
                query: '',
                eventTypes: [],
                onlyUnread: false,
            },
            date: [
                null,
                null,
            ],

            reqtId: 0,
            nextPage: 0,

            elements: [],
            read: [],
            original: null,

            scrolled: false,
            filterSheet: 'HIDDEN',

            dicts: {
                eventTypes: EVENT_TYPES,
            },
        };
    },
    computed: {
        emptyFilters() {
            const { eventTypes, onlyUnread } = this.model;
            return this.date[0] === null && eventTypes.length === 0 && onlyUnread === false;
        },
        emptyFiltersAndQuery() {
            return this.emptyFilters && this.model.query === '';
        },
        hasNext() {
            return this.reqtId === 0 || this.original?.result.length !== 0 || this.nextPage === 0;
        },
        notFound() {
            return this.original?.result.length === 0 && this.elements.length === 0 && !this.hasNext;
        },
        mobile() {
            return matchMedia('(max-device-width: 767px)').matches;
        },
        optionsEventTypes() {
            return this.dicts.eventTypes.filter(item => hasRights(item.right) && hasRoles(item.role));
        },
    },
    inject: {
        unreadNotifyCount: {
            type: Number,
            default: 0,
        },
        updateUnreadCounter: {
            type: Function,
        },
    },
    created() {
        this.$options.unwatchQuery = this.$watch((vm) => [
            vm.model.query,
            vm.model.eventTypes,
            vm.model.onlyUnread,
            vm.date,
        ], this.resetSearch);

        this.$options.unwatchNotifyRead = this.$watch((vm) =>
            vm.read, debounceAsync(this.sendRead, 10000));
    },
    mounted() {
        document.addEventListener('scroll', this.onScroll, true);
    },
    destroyed() {
        this.$options.unwatchQuery();
        this.$options.unwatchNotifyRead();
        document.removeEventListener('scroll', this.onScroll, true);
    },
    methods: {
        async loadNextPage() {
            if (!this.hasNext)
                return false;

            const reqtId = ++this.reqtId;
            const reqt = {
                currentPage: this.nextPage,
                showPerPage: 10,
                params: {
                    ...this.model,
                    dateFrom: this.date[0],
                    dateTo: this.date[1],
                },
            };

            try {
                const resp = this.original = await search(reqt);

                if (reqtId !== this.reqtId)
                    return;

                this.elements = this.elements.concat(resp.result);

                this.nextPage++;

                this.updateUnreadCounter();

                return true;
            } catch (e) {
                console.warn(e);
            }
        },
        resetSearch() {
            this.elements.splice(0);

            this.nextPage = 0;
            this.$refs.scroll.restart();
        },
        onInputQuery(query) {
            this.model.query = query;
        },
        onClear() {
            this.model.eventTypes = [];
            this.model.onlyUnread = false;
            this.date = [];
            this.resetSearch();
        },
        onScroll() {
            this.scrolled = !!this.$refs.middle.scrollTop;
        },
        onRead(id) {
            this.read.push(id);
        },
        async sendRead() {
            if (!this.read.length)
                return;

            const data = {
                ids: this.read,
            };

            await read(data);
            this.read.splice(0);

            this.updateUnreadCounter();
        },
        async readAll() {
            await readAll();

            for (const item of this.$refs.cardNotify) {
                item.read = true;
            }

            this.updateUnreadCounter();

            if (this.model.onlyUnread)
                this.resetSearch();
        },
        async close() {
            this.$emit('closeModal');
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@dsee/mixins.scss';

.notification {
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    padding-top: 32px;

    .header {
        flex-wrap: wrap;
        padding: 0 32px 10px;

        &.shadow {
            box-shadow: 0px 4px 8px rgba(28, 41, 61, 0.1);
        }

        @media (max-device-width: 767px) {
            padding: 20px 16px;
        }
    }

    .search-container {
        margin-bottom: 12px;
        width: 100%;
        display: flex;
        column-gap: 20px;
    }

    .filter {
        .row {
            display: flex;
            margin-top: 12px;
            justify-content: space-between;
        }

        .item-list {
            display: flex;
            flex-flow: row wrap;
            font-weight: 400;
            gap: 12px;

            :deep(.item) {
                .label-value {
                    .value {
                        max-width: 96px;
                    }
                }

                .dialog {
                    left: -40px;
                }
            }
        }

        .action {
            margin-left: 10px;
        }
    }

    .middle {
        flex: auto;
        @include scrollbar;
    }

    .no-elements {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        padding: 24px 0 32px;
        margin: 0 32px;
        border-radius: 4px;
        border: 1px solid rgba(16, 24, 40, 0.10);

        .icon {
            @include icon(100px, 100px);
            cursor: default;
            background-image: url('./icons/notification.png');
            margin-bottom: 16px;
        }

        .text {
            font-weight: 500;
        }
    }

    .not-found {
        text-align: center;
        margin-top: 10%;

        .title {
            color: #000000;
            font-size: 18px;
            line-height: 24px;
            margin-bottom: 12px;
            font-weight: bold;
        }

        .desc {
            font-size: 14px;
            line-height: 20px;
        }
    }

    .footer {
        flex: none;
        padding: 0;
        display: flex;
        height: 40px;
        background: linear-gradient(0deg, rgba(16, 24, 40, 0.03), rgba(16, 24, 40, 0.03)), #FFFFFF;
        box-shadow: 0px 2px 6px rgba(28, 41, 61, 0.1);

        .action {
            width: 100%;
            justify-content: center;
        }
    }

    .filter-sheet .item {
        padding: 20px 0;
    }

    .card-skeleton {
        padding-left: 24px;
        padding-right: 24px;
    }
}
</style>
<style lang="scss">
.side-nav-popup.notification {
    .header .filter .inline-date-picker .dialog {
        @media (max-device-width: 767px) {
            right: 0;
            left: unset;
        }
    }
}
</style>
